// FO-306 (batch-33, stream klant-skillmatrix) — niveau-mapping.
//
// De klant kent alleen een grove 3-puntsschaal (Basis/Goed/Expert); intern
// draaien skills op een instelbare schaal per skill (`Skill.schaalMin..schaalMax`,
// default 1-10). Deze pure helper vertaalt het klant-niveau naar een
// minimumniveau op de interne schaal, op basis van organisatiebreed instelbare
// percentages (default 40/65/85 van de schaal). Het resultaat is een geheel
// getal en blijft ALTIJD binnen [schaalMin, schaalMax].
//
// Zelfde helper gebruikt bij het voorvullen van de interne matchpagina én bij
// eventuele weergave terug naar de klant. Bewust dependency-vrij (barrel-veilig).
//
// NB: de match-engine-input (`POST /employees/match`, `minLevels`) eist minimaal
// 1. Klemmen op [schaalMin, schaalMax] kan bij `schaalMin = 0` een 0 opleveren;
// de matchpagina-koppeling past daarom aan de rand nog `Math.max(1, ...)` toe.
// Deze helper blijft puur op [schaalMin, schaalMax] (dat is wat §11 test).

// De 3-puntsschaal komt uit de gedeelde contract-types (één bron, geen drift).
import type { KlantGewenstNiveau } from '@streep/types'

export type { KlantGewenstNiveau }

export interface KlantNiveauPercentages {
  /** Percentage van de schaal voor "Basis" (0-100). */
  basis: number
  /** Percentage van de schaal voor "Goed" (0-100). */
  goed: number
  /** Percentage van de schaal voor "Expert" (0-100). */
  expert: number
}

/** Organisatiebrede defaults (FO-306 §3): Basis ≥ 40%, Goed ≥ 65%, Expert ≥ 85%. */
export const DEFAULT_KLANT_NIVEAU_PERCENTAGES: KlantNiveauPercentages = {
  basis: 40,
  goed: 65,
  expert: 85,
}

/**
 * Map een klant-3-puntsniveau naar een minimumniveau op de interne skill-schaal.
 *
 * @returns geheel getal in [schaalMin, schaalMax]
 */
export function mapKlantNiveau(
  niveau: KlantGewenstNiveau,
  schaalMin: number,
  schaalMax: number,
  percentages: KlantNiveauPercentages = DEFAULT_KLANT_NIVEAU_PERCENTAGES,
): number {
  // Degeneratieve of ongeldige schaal: niets zinnigs te interpoleren → schaalMin.
  if (
    !Number.isFinite(schaalMin) ||
    !Number.isFinite(schaalMax) ||
    schaalMax <= schaalMin
  ) {
    return Math.round(schaalMin)
  }

  const pct =
    niveau === 'BASIS'
      ? percentages.basis
      : niveau === 'GOED'
        ? percentages.goed
        : percentages.expert

  // Klem het percentage defensief op [0, 100] zodat een verkeerd ingestelde
  // config nooit buiten de schaal kan mappen.
  const clampedPct = Math.min(100, Math.max(0, pct))

  const raw = schaalMin + (clampedPct / 100) * (schaalMax - schaalMin)
  const rounded = Math.round(raw)

  // Borg de bounds ook na afronding (afrondingsruis aan de randen).
  return Math.min(schaalMax, Math.max(schaalMin, rounded))
}
